import React, { useState, useEffect } from 'react';

const ProductionUrlSwitcher = () => {
  const [currentUrl, setCurrentUrl] = useState('');
  const [customUrl, setCustomUrl] = useState('');
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);

  const predefinedUrls = [
    'http://localhost:8080',
    'https://expense-management-backend-e0draga9g5che8hk.centralindia-01.azurewebsites.net',
    // Add more URLs as needed
  ];

  useEffect(() => {
    setCurrentUrl(import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');
  }, []);

  const testUrl = async (url) => {
    setTesting(true);
    setTestResult(null);
    
    try {
      const testEndpoint = `${url}/api/health`;
      
      const response = await fetch(testEndpoint, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        setTestResult({ success: true, data, url });
      } else {
        setTestResult({ 
          success: false, 
          error: `HTTP ${response.status}: ${response.statusText}`,
          url 
        });
      }
    } catch (error) {
      setTestResult({ 
        success: false, 
        error: error.message,
        url 
      });
    } finally {
      setTesting(false);
    }
  };

  const switchToUrl = (url) => {
    // Note: This won't actually change the environment variable at runtime
    // You'll need to update the .env file and restart the dev server
    setCurrentUrl(url);
    alert(`To use ${url}, update your .env.production file and restart the application.`);
  };

  return (
    <div style={{ 
      padding: '20px', 
      border: '2px solid #8B4513', 
      margin: '20px', 
      borderRadius: '8px',
      backgroundColor: '#f9f9f9'
    }}>
      <h3 style={{ color: '#8B4513', marginBottom: '20px' }}>🔧 Production URL Switcher</h3>
      
      <div style={{ marginBottom: '20px' }}>
        <strong>Current API URL:</strong> 
        <code style={{ 
          background: '#e8f5e8', 
          padding: '4px 8px', 
          borderRadius: '4px',
          marginLeft: '10px'
        }}>
          {currentUrl}
        </code>
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4>Quick Test URLs:</h4>
        {predefinedUrls.map((url, index) => (
          <div key={index} style={{ marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
            <button
              onClick={() => testUrl(url)}
              disabled={testing}
              style={{
                padding: '8px 16px',
                background: '#8B4513',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: testing ? 'not-allowed' : 'pointer',
                opacity: testing ? 0.6 : 1
              }}
            >
              {testing ? '⏳' : '🧪'} Test
            </button>
            <code style={{ 
              background: '#f0f0f0', 
              padding: '4px 8px', 
              borderRadius: '4px',
              flex: 1
            }}>
              {url}
            </code>
            <button
              onClick={() => switchToUrl(url)}
              style={{
                padding: '8px 16px',
                background: '#4CAF50',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              📝 Use This
            </button>
          </div>
        ))}
      </div>

      <div style={{ marginBottom: '20px' }}>
        <h4>Custom URL Test:</h4>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <input
            type="text"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            placeholder="https://your-backend-url.com"
            style={{
              flex: 1,
              padding: '8px',
              border: '1px solid #ccc',
              borderRadius: '4px'
            }}
          />
          <button
            onClick={() => testUrl(customUrl)}
            disabled={testing || !customUrl}
            style={{
              padding: '8px 16px',
              background: '#8B4513',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: (testing || !customUrl) ? 'not-allowed' : 'pointer',
              opacity: (testing || !customUrl) ? 0.6 : 1
            }}
          >
            {testing ? '⏳ Testing...' : '🧪 Test'}
          </button>
        </div>
      </div>

      {testResult && (
        <div style={{ 
          marginTop: '20px',
          padding: '15px',
          borderRadius: '4px',
          background: testResult.success ? '#e8f5e8' : '#ffebee',
          border: `1px solid ${testResult.success ? '#4CAF50' : '#f44336'}`
        }}>
          <h4 style={{ 
            color: testResult.success ? '#2e7d32' : '#c62828',
            marginBottom: '10px'
          }}>
            {testResult.success ? '✅ Success!' : '❌ Failed'}
          </h4>
          <div><strong>URL:</strong> {testResult.url}</div>
          {testResult.success ? (
            <pre style={{ 
              background: 'rgba(255,255,255,0.7)', 
              padding: '10px', 
              borderRadius: '4px',
              marginTop: '10px',
              fontSize: '12px'
            }}>
              {JSON.stringify(testResult.data, null, 2)}
            </pre>
          ) : (
            <div style={{ color: '#c62828', marginTop: '10px' }}>
              <strong>Error:</strong> {testResult.error}
            </div>
          )}
        </div>
      )}

      <div style={{ 
        marginTop: '20px', 
        padding: '15px', 
        background: '#fff3cd', 
        border: '1px solid #ffeaa7',
        borderRadius: '4px'
      }}>
        <h4 style={{ color: '#856404', marginBottom: '10px' }}>💡 How to Fix Production Issues:</h4>
        <ol style={{ color: '#856404', paddingLeft: '20px' }}>
          <li>Test URLs above to find a working backend</li>
          <li>Update <code>frontend-vite/.env.production</code> with the working URL</li>
          <li>Restart your development server: <code>npm run dev</code></li>
          <li>For immediate testing, use localhost if your backend is running locally</li>
        </ol>
      </div>
    </div>
  );
};

export default ProductionUrlSwitcher;